import { AutoUpdateOptions, Placement } from '@floating-ui/dom';
import { PropertyValueMap, TemplateResult, html, CSSResultGroup } from 'lit';
import { property, queryAssignedNodes, state } from 'lit/decorators.js';
import { query } from 'lit/decorators/query.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { nanoid } from 'nanoid';
import { AutoPositionController } from '../../controllers/autoPositionOverlay';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksCloseEvent } from '../../events/blocks-close';
import { BlocksOpenEvent } from '../../events/blocks-open';
import type { AutoPositionInterface, CommonAriaLiveValue, Gap, Position } from '../../util/common-modal';
import { KEYS } from '../../util/event-keys';
import { isPopoverSupported } from '../../util/popover';
import { BlocksElement } from '../BlocksElement';
import type { TooltipAlignment, TooltipPosition } from './tooltip.model';
import styles from './tooltip.styles';
/**
 * @slot trigger - The element to open the tooltip.
 * @slot default - Tooltip content
 *
 * @fires bl-tooltip-open - Emitted when a tooltip opens
 * @fires bl-tooltip-close - Emitted when a tooltip close
 * @csspart base - The component's base wrapper.
 * @csspart wrapper - The component's wrapper.
 * @csspart part-trigger - The component's trigger slot.
 * @csspart content - The component's content wrapper.
 * @csspart part-default - The component's default slot.
 * @csspart arrow - The component's arrow wrapper.
 */
export class BlocksTooltip extends BlocksElement implements AutoPositionInterface {static styles: CSSResultGroup = styles;

  /**
   *  Append an active state on the button
   */
  @property({ type: Boolean })
  isActive: boolean | undefined;

  /**
   * Determines whether the tooltip is disabled.
   */
  @property({ type: Boolean })
  tooltipDisabled = false;

  /**
   * **Tooltip position**
   * - **bottom-start** Tooltip appears on the bottom start side
   * - **bottom-end** Tooltip appears on the bottom end side
   * - **top-start** Tooltip appears on the top start side
   * - **top-end** Tooltip appears on the top end side
   * - **left-start** Tooltip appears on the left start side
   * - **left-end** Tooltip appears on the left end side
   * - **right-start** Tooltip appears on the right start side
   * - **right-end** Tooltip appears on the right end side
   * - **top** Tooltip appears on the top center side
   * - **bottom** Tooltip appears on the bottom center side
   * - **left** Tooltip appears on the left center side
   * - **right** Tooltip appears on the right center side
   * - **auto** Tooltip appears based on space available
   * - for small elements/icons the tooltip position should set to auto/bottom/top/left/right.It cannot be set to top-start, top-end,bottom-start,bottom-end,left-start,left-end,right-start,right-end.
   * If `isInline` is false, and if specified position doesn't fit inside the viewport,
   * it will try to accommodate in other positions which fits best.
   */
  @property({ type: String })
  position: TooltipPosition = 'auto';

  /**
   * aria-describedby attribute property
   */
  @property({ type: String })
  ariaDescribedBy: string | undefined;

  /**
   * Only for accessibility purposes. Use it to announce 'tooltip' 
     when the popup opens in other languages.
   */
  @property({ type: String })
  tooltipAllyText = 'tooltip';

  /**
   * The ARIA live region politeness level for the fieldNote. To be used
   * to prioritize the announcement of helper Text
   *
   * @type {string}
   * @default "polite"
   */
  @property({ type: String })
  arialive: CommonAriaLiveValue = 'polite';

  /**
   * **Text alignment**
   * - **right** Tooltip text is aligned right
   */
  @property({ type: String })
  alignText: TooltipAlignment;

  /**
   * Determines the behavior of the panel based on the value of the `isInline` property.
   * If `isInline` is false and the popover attribute is supported by the browser,
   * the panel will be opened inside the top layer.
   * If none of the position fits and if it goes out of viewport, scroll will be enabled if `isInline` is false.
   * If `isInline` is true, the popover attribute will not be used, and the panel
   * will use the floating UI absolute strategy.
   *  @property {Boolean} isInline - Default is false.
   */
  @property({ type: Boolean })
  isInline = false;

  /**
   * Used for configuring the flip behavior.
   */
  @property({ type: Boolean })
  isFlipAllowed = false;

  /**
   * is Interactive
   * 1) Tooltip becomes active on click (not hover)
   */
  @property({ type: Boolean })
  isInteractive: boolean | undefined;

  /**
   * Long press functionality.
   * By default, components respond to long presses (a tap held for a certain duration).
   * Important: This property does not influence the behavior of tooltips activated through interactive clicks when `isInteractive` is enabled.
   */
  @property({ type: Boolean })
  disableLongPress = false;

  @state() private preventClickOnEnter = false;

  /**
   * Specify the duration in milliseconds to delay before displaying the tooltip
   */
  @property({ type: Number })
  showDelay = 0;

  /**
   * Specify the duration in milliseconds to delay before hiding the tooltip
   */
  @property({ type: Number })
  hideDelay = 0;

  /**
   * Allowed placements for tooltip to display
   * If tooltip is used with icon / small trigger elements then  its position value is auto then allowedPlacements values should be given['top','bottom','right','left'].
   */
  @property({ type: Array })
  allowedPlacements: Placement[] = [
    'bottom',
    'top',
    'left',
    'right',
    'bottom-start',
    'top-end',
    'bottom-end',
    'top-start',
    'left-start',
    'left-end',
    'right-start',
    'right-end'
  ];

  /**
   * Trigger element resize
   */
  @property({ type: Boolean })
  elementResize = false;

  /**
   * Query the .cat-c-tooltip
   */
  @query('.cat-c-tooltip')
  private _catTooltipTrigger: HTMLElement | undefined;

  /**
   * Query the cat-tooltip content element
   */
  @query('.cat-c-tooltip__content')
  private _catTooltip: HTMLElement | undefined;

  /**
   * query and extract the trigger element kept inside slot
   */
  @queryAssignedNodes({ slot: 'trigger', flatten: true }) protected childElements!: Array<BlocksElement>;

  /**
   * Query the arrow element
   */
  @query('.cat-c-tooltip__arrow')
  private _catTooltipArrow: HTMLElement | undefined;

  protected slots = new SlotController(this, 'trigger', null);

  private keepFocus = false;

  /**
   * Hold the timeout reference value
   */
  private timeoutId: number | ReturnType<typeof setTimeout> = 0;

  protected autoPositionController = new AutoPositionController(this);

  private isPopoverSupported = isPopoverSupported();

  private get hasTrigger(): boolean {
    return this.slots.hasSlotted('trigger');
  }

  private get isPopoverEnabled(): boolean {
    return this.isPopoverSupported && !this.isInline;
  }

  public getFloatingElement(): HTMLElement {
    return this._catTooltip!;
  }
  public getPosition(): Position {
    return this.position;
  }
  public getIsInline(): boolean {
    return this.isInline;
  }
  public getGap(): Gap {
    return { mainAxis: 8 };
  }
  public getReferenceElement(): HTMLElement {
    const trigger = this.hasTrigger ? this.childElements[0] : this._catTooltipTrigger;
    return trigger!;
  }

  public getAllowedPlacements(): Placement[] {
    return this.allowedPlacements;
  }
  public getArrowElement(): HTMLElement {
    return this._catTooltipArrow!;
  }
  public getIsFlipAllowed(): boolean {
    return this.isFlipAllowed;
  }
  public getAutoUpdateOptions(): AutoUpdateOptions {
    return { elementResize: this.elementResize };
  }

  /**
   * Hold the timeout reference value for long press event
   */
  private longPressTimer: number | ReturnType<typeof setTimeout> = 0;

  private longPressActivated = false;

  /**
   * Variable which checks if the tooltip is shown or not
   */
  private isShowingTooltip = false;

  /**
   * Connected Callback lifecycle
   * 1) Add event listeners
   */
  connectedCallback() {
    super.connectedCallback();
    this.ariaDescribedBy = this.ariaDescribedBy || nanoid();
    document.addEventListener('mousedown', this.handleOnClickOutside, false);
    this.addEventListener('mouseover', this.mouseOverHandler);
    this.addEventListener('mouseout', this.mouseOutHandler);
    document.addEventListener('visibilitychange', this.handleVisibility);
    document.addEventListener('keydown', this.handleKeyDownForEscape);
    this.updateComplete.then(() => {
      /*
       * Add events only when trigger is passed to the tooltip.
       * ex - breadcrumb shows tooltip without trigger
       **/
      if (!this.isInteractive && this.hasTrigger) {
        this.childElements[0].addEventListener('focusin', () => {
          if (!this.keepFocus) {
            this.open();
            this.keepFocus = true;
          }
        });
        this.childElements[0].addEventListener('focusout', () => {
          this.close();
          this.keepFocus = false;
        });
      }
    });
  }

  /**
   * Disconnected callback lifecycle
   * 1) Remove event listeners
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('mousedown', this.handleOnClickOutside, false);
    this.removeEventListener('mouseover', this.mouseOverHandler);
    this.removeEventListener('mouseout', this.mouseOutHandler);
    document.removeEventListener('keydown', this.handleKeyDownForEscape);
    if (!this.isInteractive && this.hasTrigger) {
      this.childElements[0].removeEventListener('focusin', this.open);
      this.childElements[0].removeEventListener('focusout', this.close);
    }
    document.removeEventListener('visibilitychange', this.handleVisibility);
  }

  private handleVisibility = () => {
    if (document.visibilityState === 'visible' && !this.keepFocus) {
      this.keepFocus = true;
    }
  };

  /**
   * Handle click outside the component
   * 1) Close the show hide panel on click outside
   * 2) If the tooltip is already closed then we don't care about outside clicks and we
   * can bail early. In case of programmatically opened tooltip doesn't have a trigger and should not close on outside click
   * 3) By the time a user clicks on the page the shadowRoot will almost certainly be
   * defined, but TypeScript isn't that trusting and sees this.shadowRoot as possibly
   * undefined. To work around that we'll check that we have a shadowRoot (and a
   * rendered .host) element here to appease the TypeScript compiler. This should never
   * actually be shown or run for a human end user.
   * 4) Check to see if we clicked inside the active tooltip item
   * 5) If the tooltip is active and we've clicked outside of the nav then it should
   * be closed.
   */
  private handleOnClickOutside = (event: MouseEvent) => {
    /* 2 */
    if (!this.isActive || !this.hasTrigger) {
      return;
    }

    /* 3 */
    if (!this.shadowRoot?.host) {
      throw Error('Could not determine navigation context during click handler');
    }

    /* 4 */
    const didClickInside = event.composedPath().includes(this.shadowRoot.host);

    /* 5 */
    if (this.isActive && !didClickInside) {
      this.toggle();
    }
  };

  /**
   * Keydown event
   * 1) If escape key is struck when the tooltip is active, close it
   * 2) If enter is keyed, toggle the tooltip open or close. We need these since
   * the click event is on a div so that accessibility reads this aloud to a user
   */

  private handleKeyDown(e: KeyboardEvent) {
    if (this.isActive) {
      if (e.key === KEYS.ESCAPE) {
        this.toggle(); /* 1 */
      }
    }
    if (e.key === KEYS.ENTER) {
      this.preventClickOnEnter = true;
      if (this.isInteractive) {
        this.toggle(); /* 2 */
      }
    }
  }

  /**
   * Keyup event
   * 2) If space is keyed, toggle the tooltip open or close. We need these since
   * on keydown its triggering twice and the click event is on a div so that accessibility reads this aloud to a user
   */
  private handleKeyUp(e: KeyboardEvent) {
    if (e.key === KEYS.SPACE) {
      this.preventClickOnEnter = true;
      if (this.isInteractive) {
        this.toggle(); /* 2 */
      }
    }
  }

  /**
   * Event for toggling tooltip in escape
   */
  private handleKeyDownForEscape = (e: KeyboardEvent) => {
    if (e.key === KEYS.ESCAPE && this.isShowingTooltip) {
      e.preventDefault();
      this.closeWithoutDelay();
      this.keepFocus = false;
    }
  };

  /**
   * Click event
   * 1) Toggle the active state when clicked
   * 2) Return focus to the trigger
   */
  private handleClick() {
    if (!this.preventClickOnEnter && this.isInteractive) {
      this.toggle();
    }
  }

  /**
   * Toggle the active state of tooltip
   * 1.Exit early if the tooltip is disabled to prevent toggling behavior
   */
  public toggle() {
    /* 1*/
    if (this.tooltipDisabled) {
      return;
    }
    this.isActive = !this.isActive;
    setTimeout(() => {
      this.preventClickOnEnter = false;
    }, 1);
  }

  /**
   * Emit open event when panel is opened
   */
  private emitPanelOpenEvent() {
    this.dispatch({ eventName: 'bl-tooltip-open', detailObj: { isActive: true } }) as BlocksOpenEvent;
  }

  /**
   * Emit close event when panel is closed
   */
  private emitPanelCloseEvent() {
    this.dispatch({ eventName: 'bl-tooltip-close', detailObj: { isActive: false } }) as BlocksCloseEvent;
  }

  protected updated(changedProperties: PropertyValueMap<this> | Map<PropertyKey, unknown>): void {
    if (changedProperties.has('isActive')) {
      if (this.isActive) {
        this.open();
      } else {
        this.close();
      }
    }
  }

  /**
   *
   * @param trigger-.cat-c-tooltip element
   * @param tooltipcat-tooltip content element
   */
  private returnFocusToTrigger(trigger: HTMLElement, tooltip: HTMLElement) {
    setTimeout(() => {
      if (!this.isActive) {
        trigger.focus();
      } else {
        tooltip.focus();
      }
    }, 1);
  }

  /**
   * On mouse over, add active state
   */
  private mouseOverHandler() {
    /**
     * If the tooltip is interactive, ignore the mouse handler active state
     */

    if (this.longPressActivated || this.longPressTimer === 0) {
      if (!this.isInteractive) {
        this.isActive = true;
      }
    }
    this.longPressActivated = true;
  }

  /**
   * Event handler for when a touch starts.
   * If the user continues touching without lifting their finger within this timeframe,
   * it indicates a long press, and the tooltip is toggled accordingly.
   */
  private handleTouchStart = () => {
    this.longPressActivated = true;
    this.keepFocus = true;
    if (!this.isInteractive && !this.disableLongPress) {
      this.longPressTimer = setTimeout(() => {
        this.toggle();
      }, 500);
    }
  };

  /**
   * Event handler for when a touch ends.
   * Clears the timer set during touch start, effectively canceling the long press detection.
   */
  private handleTouchEnd = () => {
    if (!this.isInteractive && !this.disableLongPress) {
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer);
      }

      this.longPressActivated = false;
    }
  };

  /**
   * On mouse out, disable active state
   */
  private mouseOutHandler() {
    /**
     * If the tooltip is interactive, ignore the mouse handler active state
     */
    if (!this.isInteractive) {
      this.isActive = false;
    }
  }

  /**
   *  Open tooltip
   * 1. Clear the timeout id.
   * 2. Close all the open tooltip
   * 3. Exit early if the tooltip is disabled to prevent emitting event and opening behavior
   */
  public open = () => {
    /* 3 */
    if (this.tooltipDisabled) {
      return;
    }
    clearTimeout(this.timeoutId); /* 1 */
    this.closeDomTooltips(); /* 2 */

    this.timeoutId = setTimeout(() => {
      this.autoPositionController.openOverlay();
      if (this._catTooltip) {
        this._catTooltip.style.display = 'block';
      }

      this.isShowingTooltip = true;

      this.emitPanelOpenEvent();

      if (this._catTooltipTrigger && this._catTooltip) {
        this.returnFocusToTrigger(this._catTooltipTrigger, this._catTooltip);
      }
    }, this.showDelay);
  };

  /**
   * Close other open tooltips except the triggered one
   */
  private closeDomTooltips() {
    document.querySelectorAll<BlocksTooltip>('cat-tooltip').forEach((tooltip: BlocksTooltip) => {
      if (tooltip !== this && tooltip.autoPositionController?.cleanup) {
        tooltip.closeWithoutDelay();
      }
    });
  }

  /**
   * Close the tooltip panel
   */
  private closePanel() {
    this.autoPositionController.closeOverlay();
    this.emitPanelCloseEvent();
  }

  /**
   * Close tooltip with out any delay
   */
  public closeWithoutDelay = () => {
    this.closePanel();
    if (this.isActive) {
      this.isActive = false;
    }
    this.isShowingTooltip = false;
  };

  /**
   * Close tooltip
   */
  public close = () => {
    clearTimeout(this.timeoutId);
    if (!this.autoPositionController.cleanup) {
      return;
    }
    this.timeoutId = setTimeout(() => {
      this.closePanel();

      if (this._catTooltipTrigger && this._catTooltip) {
        this.returnFocusToTrigger(this._catTooltipTrigger, this._catTooltip);
      }

      this.isShowingTooltip = false;
    }, this.hideDelay);
  };

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-tooltip', {
      'cat-is-active': this.isActive === true,
      'cat-c-tooltip--text-align-right': this.alignText === 'right'
    });

    return html`
      <div part="base" class="${componentClassNames}" aria-live=${this.arialive}>
        <div
          part="wrapper"
          aria-describedby="${ifDefined(this.ariaDescribedBy)}"
          @click=${this.handleClick}
          @touchstart=${this.handleTouchStart}
          @touchend=${this.handleTouchEnd}
          @keydown=${this.handleKeyDown}
          @keyup=${this.handleKeyUp}
        >
          <slot part="part-trigger" name="trigger"></slot>
        </div>
        <div
          popover=${ifDefined(this.isPopoverEnabled ? 'manual' : undefined)}
          part="content"
          class="cat-c-tooltip__content"
          id="${ifDefined(this.ariaDescribedBy)}"
          role="tooltip"
        >
          <span class="cat-u-is-vishidden">${this.tooltipAllyText}</span>
          <slot part="part-default" class="cat-c-tooltip__text"></slot>
          <div part="arrow" class="cat-c-tooltip__arrow"></div>
        </div>
      </div>
    `;
  }
}

if (customElements.get('cat-tooltip') === undefined) {
  customElements.define('cat-tooltip', BlocksTooltip);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-tooltip': BlocksTooltip;
  }
}
